import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal, recomputeUpsellAmount } from "@/hooks/use-budget-lines";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";
import { useAuth } from "@/contexts/AuthContext";
import { logProjectEvent } from "@/hooks/use-project-events";
import { assertBillingModeChangeAllowed } from "@/hooks/upsell-billing-mode-guard";
import type { UpsellStatus, UpsellBillingMode } from "./use-upsells";

// VIB-1126 (WP10 GJELD-2): mutations splittet per ansvar. Linje-nivå mutations
// (budget_lines) ligger i use-upsell-line-mutations, og PDF-import + vedlegg i
// use-upsell-import. Re-eksporteres her så eksisterende imports virker uendret.
export {
  useAddUpsellLineFromTemplate,
  useAddUpsellLineFreeText,
  useUpdateUpsellLine,
  useDeleteUpsellLine,
} from "./use-upsell-line-mutations";
export {
  useImportUpsellFromPdf,
  getUpsellAttachmentUrl,
  type ImportUpsellLine,
} from "./use-upsell-import";
export { useSaveUpsellDraft } from "./use-save-upsell-draft";
export { assertBillingModeChangeAllowed } from "./upsell-billing-mode-guard";

/** Opprett tomt mersalg (container). Linjer legges til etterpå.
 *
 * VIB-665: Nye mersalg opprettes som `draft` med mindre `approved` settes
 * eksplisitt (når PL huker av "Godkjent av kunde direkte"). Drafts teller
 * IKKE i BF før PL/admin klikker Godkjenn.
 */
export function useCreateUpsellContainer() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      description?: string;
      status?: UpsellStatus;
      /** VIB-994: faktureringsform ved opprettelse. Default 'fixed' (DB-default dekker det samme). */
      billing_mode?: UpsellBillingMode;
    }): Promise<string> => {
      const status: UpsellStatus = input.status ?? "draft";
      const { data, error } = await supabase
        .from("upsells")
        .insert({
          project_id: input.project_id,
          description: input.description ?? "Nytt mersalg",
          amount_excl_vat: 0,
          kind: "cost",
          status,
          approved_at: status === "approved" ? new Date().toISOString() : null,
          ...(input.billing_mode ? { billing_mode: input.billing_mode } : {}),
        })
        .select("id")
        .single();
      if (error) throw error;
      await recomputeProjectTotal(input.project_id);
      return data.id as string;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke opprette mersalg: ${(err as Error).message}`);
    },
  });
}

export function useUpdateUpsellContainer() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      project_id: string;
      patch: {
        description?: string;
        category?: string | null;
        // VIB-665: overhead/rabatt per upsell
        overhead_pct?: number | null;
        discount_pct?: number;
        discount_amount?: number;
        /** VIB-994: faktureringsform — se assertBillingModeChangeAllowed for vernet. */
        billing_mode?: UpsellBillingMode;
      };
    }) => {
      let previousBillingMode: UpsellBillingMode | undefined;
      let billingModeChanged = false;

      if (input.patch.billing_mode !== undefined) {
        const { data: current, error: fetchErr } = await supabase
          .from("upsells")
          .select("billing_mode, added_to_invoice_id")
          .eq("id", input.id)
          .maybeSingle();
        if (fetchErr) throw fetchErr;
        previousBillingMode = current?.billing_mode as UpsellBillingMode | undefined;
        billingModeChanged = previousBillingMode !== input.patch.billing_mode;
        if (billingModeChanged) {
          await assertBillingModeChangeAllowed(input.id, current?.added_to_invoice_id ?? null);
        }
      }

      const { error } = await supabase
        .from("upsells")
        .update(input.patch)
        .eq("id", input.id);
      if (error) throw error;
      // Overhead/rabatt eller bytte av faktureringsform endrer kundepris-cachen
      // (T&M tvinger amount_excl_vat = 0; fastpris = linjer + OH − rabatt).
      if (
        billingModeChanged ||
        input.patch.overhead_pct !== undefined ||
        input.patch.discount_pct !== undefined ||
        input.patch.discount_amount !== undefined
      ) {
        await recomputeUpsellAmount(input.id);
        await recomputeProjectTotal(input.project_id);
      }
      // VIB-994: audit-event for billing_mode-endring (økonomidata — aktør er
      // FAKTISK innlogget bruker, ikke impersonert, jf. CLAUDE.md audit-regel 1).
      if (billingModeChanged) {
        await logProjectEvent({
          projectId: input.project_id,
          userId: user?.id ?? null,
          eventType: "upsell_billing_mode_changed",
          entityType: "upsell",
          entityId: input.id,
          payload: {
            old_billing_mode: previousBillingMode ?? "fixed",
            new_billing_mode: input.patch.billing_mode,
          },
        });
      }
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      if (vars.patch.billing_mode !== undefined) {
        await invalidateFor(qc, "project_events", { projectId: vars.project_id });
      }
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Oppdatering feilet: ${(err as Error).message}`);
    },
  });
}

/** VIB-665: Godkjenn et draft-mersalg. Setter status='approved' + audit-felter. */
export function useApproveUpsell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      project_id: string;
      approved_by: string;
    }) => {
      const { error } = await supabase
        .from("upsells")
        .update({
          status: "approved",
          approved_by: input.approved_by,
          approved_at: new Date().toISOString(),
        })
        .eq("id", input.id);
      if (error) throw error;
      await recomputeProjectTotal(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      toast.success("Mersalg godkjent");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Godkjenning feilet: ${(err as Error).message}`);
    },
  });
}

/** VIB-665: Myk sletting (status='deleted'). Beholder historikk + revisjonsspor.
 *
 * VIB-725: DB-trigger `trg_cleanup_deleted_upsell_lines` hard-sletter
 * tilhørende budget_lines når status går til 'deleted'. Vi invaliderer
 * budget_lines- og project-hours-cachene så fantom-rader forsvinner fra UI
 * umiddelbart uten side-refresh.
 */
export function useSoftDeleteUpsell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: { id: string; project_id: string }) => {
      const { error } = await supabase
        .from("upsells")
        .update({ status: "deleted" })
        .eq("id", input.id);
      if (error) throw error;
      await recomputeProjectTotal(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      // VIB-725: DB-trigger hard-sletter budget_lines når status='deleted'
      // — invalider budget_lines-lesere så fantom-rader forsvinner uten reload.
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      toast.success("Mersalg slettet");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Sletting feilet: ${(err as Error).message}`);
    },
  });
}

/**
 * Knytt en upsell til en eksisterende faktura. Brukes når mersalg-fakturaen
 * er opprettet manuelt eller synket fra PO uten kobling tilbake til
 * upsell-raden — fjerner mersalget fra "Ufakturert mersalg"-listen.
 */
export function useLinkUpsellToInvoice() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      upsell_id: string;
      project_id: string;
      invoice_id: string | null;
    }) => {
      const { error } = await supabase
        .from("upsells")
        .update({ added_to_invoice_id: input.invoice_id })
        .eq("id", input.upsell_id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      toast.success("Mersalg knyttet til faktura");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kobling feilet: ${(err as Error).message}`);
    },
  });
}

/**
 * Oppretter et mersalg fra et bilag (bilag-godkjenning + viderefakturering).
 *
 * Et mersalg er en container (upsells) + linjer (budget_lines). Kundeprisen
 * (linjer + overhead − rabatt) cachees i `amount_excl_vat` via
 * recomputeUpsellAmount — en flat verdi uten linje gir et TOMT mersalg
 * (feedback 25.06). Derfor opprettes container + én linje med bilagsbeløpet.
 *
 * estimated_cost = 0: kostnaden er allerede bokført via bilaget; mersalgs-
 * linjen representerer kun den gjenvunne inntekten (viderefakturering).
 */
export function useCreateUpsell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      description: string;
      amount_excl_vat: number;
      category?: string;
      source_voucher_id?: string | null;
    }) => {
      const { data: container, error: containerErr } = await supabase
        .from("upsells")
        .insert({
          project_id: input.project_id,
          description: input.description,
          amount_excl_vat: 0,
          category: input.category ?? null,
          source_voucher_id: input.source_voucher_id ?? null,
          kind: "cost",
          status: "approved",
          approved_at: new Date().toISOString(),
        })
        .select("id")
        .single();
      if (containerErr) throw containerErr;

      const upsellId = container.id as string;
      const phase = input.category === "reise" ? "reise" : "production";
      const { error: lineErr } = await supabase.from("budget_lines").insert({
        project_id: input.project_id,
        upsell_id: upsellId,
        phase,
        description: input.description,
        cost_type: "external",
        unit: "stk",
        quantity: 0,
        quantity_hours: 0,
        amount: input.amount_excl_vat,
        estimated_cost: 0,
      });
      if (lineErr) throw lineErr;

      await recomputeUpsellAmount(upsellId);
      await recomputeProjectTotal(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      toast.success("Mersalg opprettet");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error("Noe gikk galt");
    },
  });
}
